/**
 * First-Party Website Analytics Tracker (Phase 2.17)
 *
 * Lightweight, privacy-first client-side tracking library for SMA.
 * Tracks anonymous visitor sessions, UTM campaigns, page views, and interactions
 * without collecting PII.
 */

const STORAGE_KEY_VID = 'sma_vid'
const STORAGE_KEY_SID = 'sma_sid'
const STORAGE_KEY_SID_TIMESTAMP = 'sma_sid_ts'
const STORAGE_KEY_UTM = 'sma_utm_params'
const STORAGE_KEY_FIRST_UTM = 'sma_first_utm_params'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

let lastTrackedEventKey = ''
let lastTrackedTimestamp = 0

function generateId(prefix = 'id') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, '')}`.slice(0, 48)
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export function getOrCreateVisitorId() {
  try {
    let vid = localStorage.getItem(STORAGE_KEY_VID)
    if (!vid || typeof vid !== 'string' || vid.length < 8) {
      vid = generateId('vid')
      localStorage.setItem(STORAGE_KEY_VID, vid)
    }
    return vid
  } catch {
    return generateId('vid')
  }
}

export function getOrCreateSessionId() {
  try {
    const now = Date.now()
    const storedSid = sessionStorage.getItem(STORAGE_KEY_SID)
    const storedTs = Number(sessionStorage.getItem(STORAGE_KEY_SID_TIMESTAMP)) || 0

    if (storedSid && storedTs > 0 && now - storedTs < SESSION_TIMEOUT_MS) {
      sessionStorage.setItem(STORAGE_KEY_SID_TIMESTAMP, String(now))
      return storedSid
    }

    // Generate new session
    const newSid = generateId('sid')
    sessionStorage.setItem(STORAGE_KEY_SID, newSid)
    sessionStorage.setItem(STORAGE_KEY_SID_TIMESTAMP, String(now))
    return newSid
  } catch {
    return generateId('sid')
  }
}

export function captureUtmParams() {
  try {
    if (typeof window === 'undefined') return {}
    const params = new URLSearchParams(window.location.search)
    const utm = {}

    const source = params.get('utm_source') || params.get('source')
    const medium = params.get('utm_medium') || params.get('medium')
    const campaign = params.get('utm_campaign') || params.get('campaign')
    const term = params.get('utm_term') || params.get('term')
    const content = params.get('utm_content') || params.get('content')

    if (source || campaign) {
      if (source) utm.source = source.slice(0, 100)
      if (medium) utm.medium = medium.slice(0, 100)
      if (campaign) utm.campaign = campaign.slice(0, 150)
      if (term) utm.term = term.slice(0, 150)
      if (content) utm.content = content.slice(0, 150)

      sessionStorage.setItem(STORAGE_KEY_UTM, JSON.stringify(utm))

      // Phase 2.17.1: Persist first-touch UTM to localStorage so the original
      // acquisition source survives tab/browser close. Only set if not already
      // stored — first-touch is never overwritten by subsequent visits.
      try {
        const existingFirst = localStorage.getItem(STORAGE_KEY_FIRST_UTM)
        if (!existingFirst) {
          localStorage.setItem(STORAGE_KEY_FIRST_UTM, JSON.stringify(utm))
        }
      } catch {
        // localStorage may be unavailable (private mode) — non-fatal
      }

      return utm
    }

    const saved = sessionStorage.getItem(STORAGE_KEY_UTM)
    if (saved) {
      return JSON.parse(saved) || {}
    }
  } catch {
    // ignore
  }
  return {}
}

/**
 * Retrieve the first-touch UTM params (original acquisition source).
 * These persist across sessions in localStorage and are never overwritten.
 */
export function getFirstTouchUtm() {
  try {
    if (typeof window === 'undefined') return {}
    const saved = localStorage.getItem(STORAGE_KEY_FIRST_UTM)
    if (saved) {
      return JSON.parse(saved) || {}
    }
  } catch {
    // ignore
  }
  return {}
}

export function detectDevice() {
  if (typeof window === 'undefined') {
    return { deviceType: 'desktop', browser: 'Other', operatingSystem: 'Other' }
  }

  const ua = navigator.userAgent || ''
  let deviceType = 'desktop'
  if (/iPad|Tablet|PlayBook/i.test(ua) || (window.innerWidth >= 640 && window.innerWidth <= 1024 && 'ontouchstart' in window)) {
    deviceType = 'tablet'
  } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(ua) || window.innerWidth < 640) {
    deviceType = 'mobile'
  }

  let browser = 'Other'
  if (/Edg\//i.test(ua)) browser = 'Edge'
  else if (/Chrome\//i.test(ua)) browser = 'Chrome'
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari'
  else if (/Firefox\//i.test(ua)) browser = 'Firefox'
  else if (/Opera|OPR\//i.test(ua)) browser = 'Opera'
  else if (/SamsungBrowser/i.test(ua)) browser = 'Samsung Internet'

  let operatingSystem = 'Other'
  if (/Windows/i.test(ua)) operatingSystem = 'Windows'
  else if (/iPhone|iPad|iPod/i.test(ua)) operatingSystem = 'iOS'
  else if (/Mac OS X|Macintosh/i.test(ua)) operatingSystem = 'macOS'
  else if (/Android/i.test(ua)) operatingSystem = 'Android'
  else if (/Linux/i.test(ua)) operatingSystem = 'Linux'

  return { deviceType, browser, operatingSystem }
}

export function getTrackingContext() {
  const visitorId = getOrCreateVisitorId()
  const sessionId = getOrCreateSessionId()
  const utm = captureUtmParams()
  const firstTouch = getFirstTouchUtm()
  return {
    visitorId,
    sessionId,
    utmSource: utm.source || null,
    utmMedium: utm.medium || null,
    utmCampaign: utm.campaign || null,
    utmTerm: utm.term || null,
    utmContent: utm.content || null,
    // First-touch (original acquisition) — survives tab/browser close
    firstUtmSource: firstTouch.source || null,
    firstUtmMedium: firstTouch.medium || null,
    firstUtmCampaign: firstTouch.campaign || null,
    firstUtmTerm: firstTouch.term || null,
    firstUtmContent: firstTouch.content || null,
  }
}

export function isPortalRoute(pathname = '') {
  return /^\/(admin|finance|trainer|student)(\/|$)/i.test(pathname)
}

export function isUserAuthenticated() {
  try {
    if (typeof window === 'undefined') return false
    const token = localStorage.getItem('token')
    if (!token) return false
    return true
  } catch {
    return false
  }
}

/**
 * Dispatch analytics event to backend
 */
export async function trackEvent(eventType, eventData = {}, options = {}) {
  try {
    if (typeof window === 'undefined') return

    const pagePath = options.pagePath || window.location.pathname || '/'

    // Centralized Frontend Exclusion:
    // Do NOT generate website analytics for authenticated application users or internal portal pages.
    if (isUserAuthenticated() || isPortalRoute(pagePath)) {
      return
    }

    const visitorId = getOrCreateVisitorId()
    const sessionId = getOrCreateSessionId()
    const utm = captureUtmParams()
    const device = detectDevice()

    const pageTitle = options.pageTitle || document.title || 'Synergy Multimedia Academy'
    const referrer = options.referrer || document.referrer || ''

    // Deduplication check: prevent duplicate identical event in quick succession (<600ms)
    const eventKey = `${eventType}:${pagePath}:${JSON.stringify(eventData)}`
    const now = Date.now()
    if (eventKey === lastTrackedEventKey && now - lastTrackedTimestamp < 600) {
      return
    }
    lastTrackedEventKey = eventKey
    lastTrackedTimestamp = now

    const payload = {
      visitorId,
      sessionId,
      eventType,
      pagePath,
      pageTitle,
      referrer,
      source: utm.source || null,
      medium: utm.medium || null,
      campaign: utm.campaign || null,
      term: utm.term || null,
      content: utm.content || null,
      deviceType: device.deviceType,
      browser: device.browser,
      operatingSystem: device.operatingSystem,
      eventData,
    }

    // Must use the same API base as the rest of the app (services/api.js) — on production
    // the frontend (Vercel) and backend (Railway) are different origins, so a hardcoded
    // relative '/api/...' path here would hit the frontend's own static host instead of
    // the API, which responds 405 Method Not Allowed to a POST against its SPA rewrite.
    const API_BASE = import.meta.env.VITE_API_URL || '/api'
    const apiUrl = `${API_BASE.replace(/\/$/, '')}/analytics/track`

    const token = localStorage.getItem('token')
    const headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // Try fetch with keepalive first
    if (typeof fetch === 'function') {
      fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // silent fail
      })
    }
  } catch {
    // Fail silently to never interrupt visitor experience
  }
}

export function trackPageView(pagePath, pageTitle) {
  trackEvent('page_view', {}, { pagePath, pageTitle })
}

export function trackCourseView(courseCode, courseTitle) {
  trackEvent('course_view', { courseCode, courseTitle })
}

export function trackCtaClick(ctaId, ctaLabel, destination) {
  trackEvent('cta_click', { ctaId, ctaLabel, destination })
}

export function trackAdvisorOpened() {
  trackEvent('advisor_opened', { source: 'website' })
}

export function trackAdvisorStarted(topic) {
  trackEvent('advisor_started', { topic })
}

export function trackLeadCreated(leadPublicId, courseCode) {
  trackEvent('lead_created', { leadPublicId, courseCode })
}

export function trackApplicationStarted(courseCode) {
  trackEvent('application_started', { courseCode })
}

export function trackMasterclassView(masterclassId, topic, speaker) {
  trackEvent('masterclass_view', { masterclassId, topic, speaker })
}

export function trackMasterclassRegStarted(masterclassId, topic) {
  trackEvent('masterclass_registration_started', { masterclassId, topic })
}

export function trackMasterclassRegCompleted(masterclassId, registrationNumber) {
  trackEvent('masterclass_registration_completed', { masterclassId, registrationNumber })
}

export function trackMasterclassPassViewed(registrationNumber) {
  trackEvent('masterclass_pass_viewed', { registrationNumber })
}

export function trackMasterclassPaymentStarted(masterclassId, registrationNumber, paymentMethod) {
  trackEvent('masterclass_payment_started', { masterclassId, registrationNumber, paymentMethod })
}

export function trackMasterclassPaymentCompleted(masterclassId, registrationNumber, amount, invoiceNumber) {
  trackEvent('masterclass_payment_completed', { masterclassId, registrationNumber, amount, invoiceNumber })
}

export function trackMasterclassPaymentProofSubmitted(masterclassId, registrationNumber, paymentMethod) {
  trackEvent('masterclass_payment_proof_submitted', { masterclassId, registrationNumber, paymentMethod })
}

export function trackMasterclassPaymentVerified(masterclassId, registrationNumber, amount) {
  trackEvent('masterclass_payment_verified', { masterclassId, registrationNumber, amount })
}

export function trackMasterclassPaymentRejected(masterclassId, registrationNumber, reason) {
  trackEvent('masterclass_payment_rejected', { masterclassId, registrationNumber, reason })
}

